import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, throwError, of, from, forkJoin } from 'rxjs';
import { catchError, map, switchMap, toArray, concatMap } from 'rxjs/operators';

export interface NowSingle<T = any> { result: T }
export interface NowList<T = any> { result: T[] }
export interface SnApiAuth {
  user: string;
  pass: string;
}
export interface SnDeleteTableResult {
  table: string;
  matched: number;
  deleted: number;
}

export interface SnClaireCleanupResult {
  totalMatched: number;
  totalDeleted: number;
  tables: SnDeleteTableResult[];
}

export interface SnVendorOnboardingCleanupResult {
  totalMatched: number;
  totalDeleted: number;
  tables: SnDeleteTableResult[];
}

export interface OrderNowRequest {
  sysparm_quantity?: string | number;
  variables: Record<string, any>;
}
export interface OrderNowResponse {
  result?: {
    request_id?: string;
    request_number?: string;
    sys_id?: string;
    [k: string]: any;
  };
}

@Injectable({ providedIn: 'root' })
export class SnApiService {
  private readonly serviceNowProxyBase = '/api/servicenow';
  private readonly localProxyBase = '/sn-api';

  private readonly CUSTOMER_TABLE = 'u_rs_demo_rag___claire___customers';
  private readonly CATALOG_ITEM_SYS_ID = '167bd0ca4774aa104c57c502d16d43a2';
  private readonly CLAIRE_CUSTOMERS_TABLE = 'u_rs_demo_rag___claire___customers';
  private readonly CLAIRE_STATUS_SUMMARY_TABLE = 'u_rs_demo_rag___claire___status_summary';
  private readonly CLAIRE_DOCUMENTS_TABLE = 'u_rs_demo_rag___claire___documents';
  private readonly CLAIRE_ESIGNATURES_TABLE = 'u_rs_demo_rag___claire___esignatures';
  private readonly CLAIRE_SAP_CREATION_EVENT_TABLE = 'u_rs_demo_rag___claire___sap_creation_event';
  private readonly VENDOR_ONBOARDING_TABLE = 'u_angular___vendor_onboard_form';
  private readonly JASON_INVENTORY_LEVELS_TABLES = [
    'u_rs_demo_rag___jason___inventory_levels'
  ];

  private readonly integrationUser = 'nh-IntegrationUser';
  private readonly integrationPass = '';

  private readonly DEMO_CLAIRE_USER = 'demo.claire';
  private readonly DEMO_CLAIRE_PASS = '';
  private readonly DEMO_JOHN_USER = 'demo.john';
  private readonly DEMO_JASON_USER = 'demo.jason';
  private readonly DEMO_JASON_PASS = '';

  constructor(private http: HttpClient) {}

  getBaseUrl(): string {
    return this.serviceNowProxyBase;
  }

  createCustomerRecord(payload: any): Observable<NowSingle> {
    return this.createTableRecord(this.CUSTOMER_TABLE, payload, this.getIntegrationAuth());
  }

  createTableRecord<T = any>(
    table: string,
    payload: Record<string, any>,
    auth?: SnApiAuth
  ): Observable<NowSingle<T>> {
    const url = `${this.serviceNowProxyBase}/table/${encodeURIComponent(table)}`;
    const params = this.withProfileParam({ sysparm_input_display_value: 'true' }, auth);
    return this.http.post<NowSingle<T>>(url, payload, {
      params,
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<NowSingle<T>>) => this.withIdentityFromHeaders(response))
    );
  }

  updateTableRecord<T = any>(
    table: string,
    sysId: string,
    payload: Record<string, any>,
    auth?: SnApiAuth
  ): Observable<NowSingle<T>> {
    const url = `${this.serviceNowProxyBase}/table/${encodeURIComponent(table)}/${encodeURIComponent(sysId)}`;
    const params = this.withProfileParam({ sysparm_input_display_value: 'true' }, auth);
    return this.http.patch<NowSingle<T>>(url, payload, { params });
  }

  deleteTableRecord<T = any>(
    table: string,
    sysId: string,
    auth?: SnApiAuth
  ): Observable<T> {
    const url = `${this.serviceNowProxyBase}/table/${encodeURIComponent(table)}/${encodeURIComponent(sysId)}`;
    const params = this.withProfileParam({}, auth);
    return this.http.delete<T>(url, { params });
  }

  getTableRows<T = any>(
    table: string,
    params: Record<string, string> = {},
    auth?: SnApiAuth
  ): Observable<NowList<T>> {
    const url = `${this.serviceNowProxyBase}/table/${encodeURIComponent(table)}`;
    return this.http.get<NowList<T>>(url, { params: this.withProfileParam(params, auth) });
  }

  getTableStats<T = any>(
    table: string,
    params: Record<string, string> = {},
    auth?: SnApiAuth
  ): Observable<T> {
    const url = `${this.serviceNowProxyBase}/stats/${encodeURIComponent(table)}`;
    return this.http.get<T>(url, { params: this.withProfileParam(params, auth) });
  }

  uploadAttachment<T = any>(
    table: string,
    sysId: string,
    file: File,
    auth?: SnApiAuth
  ): Observable<NowSingle<T>> {
    const formData = new FormData();
    formData.append('file', file, file.name || 'attachment');
    const params = this.withProfileParam(
      {
        table_name: table,
        table_sys_id: sysId,
        file_name: file.name || 'attachment'
      },
      auth
    );
    return this.http.post<NowSingle<T>>(`${this.serviceNowProxyBase}/attachment/file`, formData, { params });
  }

  getClaireCustomers(limit = 200): Observable<NowList<any>> {
    return this.getTableRows(
      this.CLAIRE_CUSTOMERS_TABLE,
      {
        sysparm_limit: String(limit),
        sysparm_display_value: 'true',
        sysparm_query: 'ORDERBYDESCsys_created_on',
        sysparm_fields: [
          'sys_id',
          'sys_created_on',
          'sys_created_by',
          'u_created_by_name',
          'u_customer_id',
          'u_legal_name',
          'u_dba',
          'u_billing_address',
          'u_registered_address',
          'u_shipping_address',
          'u_tax_id',
          'u_state'
        ].join(',')
      },
      this.getClaireAuth()
    );
  }

  getClaireTableRows<T = any>(
    table: string,
    params: Record<string, string> = {}
  ): Observable<NowList<T>> {
    return this.getTableRows<T>(table, params, this.getClaireAuth());
  }

  getClaireStatusSummary(limit = 200): Observable<NowList<any>> {
    return this.getClaireTableRows(this.CLAIRE_STATUS_SUMMARY_TABLE, {
      sysparm_limit: String(limit),
      sysparm_display_value: 'true',
      sysparm_fields: [
        'sys_id',
        'u_customer_id',
        'u_stage',
        'u_state',
        'u_status',
        'u_cycle_time_days',
        'u_sla_days',
        'u_next_action',
        'u_owner',
        'stage',
        'state',
        'status',
        'cycle_time_days',
        'sla_days',
        'next_action',
        'owner'
      ].join(',')
    });
  }

  getClaireDocuments(limit = 200): Observable<NowList<any>> {
    return this.getClaireTableRows(this.CLAIRE_DOCUMENTS_TABLE, {
      sysparm_limit: String(limit),
      sysparm_display_value: 'true',
      sysparm_fields: [
        'sys_id',
        'u_customer_id',
        'u_document_type',
        'u_state',
        'u_status',
        'u_stage',
        'document_type',
        'state',
        'status',
        'stage'
      ].join(',')
    });
  }

  getClaireEsignatures(limit = 200): Observable<NowList<any>> {
    return this.getClaireTableRows(this.CLAIRE_ESIGNATURES_TABLE, {
      sysparm_limit: String(limit),
      sysparm_display_value: 'true',
      sysparm_fields: [
        'sys_id',
        'u_customer_id',
        'u_esign_status',
        'u_status',
        'u_state',
        'u_last_event',
        'esign_status',
        'status',
        'state',
        'last_event'
      ].join(',')
    });
  }

  getClaireSapCreationEvents(limit = 200): Observable<NowList<any>> {
    return this.getClaireTableRows(this.CLAIRE_SAP_CREATION_EVENT_TABLE, {
      sysparm_limit: String(limit),
      sysparm_display_value: 'true',
      sysparm_fields: [
        'sys_id',
        'u_customer_id',
        'u_result',
        'u_status',
        'u_state',
        'result',
        'status',
        'state'
      ].join(',')
    });
  }

  clearClaireCreatedRecords(): Observable<SnClaireCleanupResult> {
    const auth = this.getClaireAuth();
    const creatorMatchesResetTargets = (value: any): boolean => {
      const raw = String(value ?? '').trim().toLowerCase();
      if (!raw) return false;
      const normalized = raw.replace(/[^a-z0-9]/g, '');
      return normalized === 'nhintegrationuser' || normalized === 'democlaire';
    };

    const summarizeDeletes = (table: string, matchedIds: string[]) => {
      if (!matchedIds.length) {
        return of({ table, matched: 0, deleted: 0 } as SnDeleteTableResult);
      }
      return from(matchedIds).pipe(
        concatMap((sysId) =>
          this.deleteTableRecord(table, sysId, auth).pipe(
            map(() => true),
            catchError(() => of(false))
          )
        ),
        toArray(),
        map((results) => ({
          table,
          matched: matchedIds.length,
          deleted: results.filter(Boolean).length
        } as SnDeleteTableResult))
      );
    };

    return this.getTableRows<any>(
      this.CLAIRE_CUSTOMERS_TABLE,
      {
        sysparm_limit: '1000',
        sysparm_display_value: 'true',
        sysparm_query: 'ORDERBYDESCsys_created_on',
        sysparm_fields: 'sys_id,sys_created_by,u_created_by_name,u_customer_id'
      },
      auth
    ).pipe(
      map((response) => Array.isArray(response?.result) ? response.result : []),
      switchMap((customerRows) => {
        const matchedCustomers = customerRows.filter((row) =>
          creatorMatchesResetTargets(row?.sys_created_by)
        );
        const matchedCustomerIds = new Set(
          matchedCustomers
            .map((row) => String(row?.u_customer_id || '').trim())
            .filter((id) => !!id)
        );
        const matchedCustomerSysIds = matchedCustomers
          .map((row) => String(row?.sys_id || '').trim())
          .filter((id) => !!id);

        const relatedTables = [
          this.CLAIRE_STATUS_SUMMARY_TABLE,
          this.CLAIRE_DOCUMENTS_TABLE,
          this.CLAIRE_ESIGNATURES_TABLE,
          this.CLAIRE_SAP_CREATION_EVENT_TABLE
        ];

        const relatedDeletes$ = relatedTables.map((table) =>
          this.getTableRows<any>(
            table,
            {
              sysparm_limit: '1000',
              sysparm_display_value: 'true',
              sysparm_query: 'ORDERBYDESCsys_created_on',
              sysparm_fields: 'sys_id,sys_created_by,u_created_by_name,u_customer_id'
            },
            auth
          ).pipe(
            map((response) => Array.isArray(response?.result) ? response.result : []),
            map((rows) =>
              rows
                .filter((row) => {
                  const customerId = String(row?.u_customer_id || '').trim();
                  return customerId && matchedCustomerIds.has(customerId);
                })
                .map((row) => String(row?.sys_id || '').trim())
                .filter((id) => !!id)
            ),
            switchMap((matchedIds) => summarizeDeletes(table, matchedIds)),
            catchError(() => of({ table, matched: 0, deleted: 0 } as SnDeleteTableResult))
          )
        );

        return forkJoin([
          summarizeDeletes(this.CLAIRE_CUSTOMERS_TABLE, matchedCustomerSysIds),
          ...relatedDeletes$
        ]);
      }),
      map((tablesResult) => ({
        totalMatched: tablesResult.reduce((sum, row) => sum + row.matched, 0),
        totalDeleted: tablesResult.reduce((sum, row) => sum + row.deleted, 0),
        tables: tablesResult
      }))
    );
  }

  clearVendorOnboardingDemoRecords(): Observable<SnVendorOnboardingCleanupResult> {
    const authCandidates = this.getVendorOnboardingCleanupAuthCandidates();
    const creatorMatchesResetTargets = (value: any): boolean => {
      const raw = String(value ?? '').trim().toLowerCase();
      if (!raw) return false;
      const normalized = raw.replace(/[^a-z0-9]/g, '');
      return normalized === this.DEMO_JOHN_USER.replace(/[^a-z0-9]/g, '');
    };

    const summarizeDeletes = (table: string, matchedIds: string[]) => {
      if (!matchedIds.length) {
        return of({ table, matched: 0, deleted: 0 } as SnDeleteTableResult);
      }
      return from(matchedIds).pipe(
        concatMap((sysId) =>
          this.tryAuthCandidates(authCandidates, (auth) =>
            this.deleteTableRecord(table, sysId, auth).pipe(map(() => true))
          ).pipe(catchError(() => of(false)))
        ),
        toArray(),
        map((results) => ({
          table,
          matched: matchedIds.length,
          deleted: results.filter(Boolean).length
        } as SnDeleteTableResult))
      );
    };

    return this.tryAuthCandidates(
      authCandidates,
      (auth) =>
        this.getTableRows<any>(
          this.VENDOR_ONBOARDING_TABLE,
          {
            sysparm_limit: '10000',
            sysparm_display_value: 'true',
            sysparm_query: 'ORDERBYDESCsys_created_on',
            sysparm_fields: 'sys_id,sys_created_by'
          },
          auth
        )
    ).pipe(
      map((response) => Array.isArray(response?.result) ? response.result : []),
      map((rows) =>
        rows
          .filter((row) => creatorMatchesResetTargets(row?.sys_created_by))
          .map((row) => String(row?.sys_id || '').trim())
          .filter((id) => !!id)
      ),
      switchMap((matchedIds) => summarizeDeletes(this.VENDOR_ONBOARDING_TABLE, matchedIds)),
      map((tableResult) => ({
        totalMatched: tableResult.matched,
        totalDeleted: tableResult.deleted,
        tables: [tableResult]
      }))
    );
  }

  private getVendorOnboardingCleanupAuthCandidates(): SnApiAuth[] {
    const defaultAuth: SnApiAuth = { user: '', pass: '' };
    const unique = new Map<string, SnApiAuth>();
    [defaultAuth, this.getIntegrationAuth(), this.getClaireAuth(), this.getJasonAuth()].forEach((candidate) => {
      unique.set(candidate.user, candidate);
    });
    return Array.from(unique.values());
  }

  getJasonInventoryLevels(limit = 200): Observable<NowList<any>> {
    const params = {
      sysparm_limit: String(limit),
      sysparm_display_value: 'true'
    };
    return this.tryTablesWithAuthFallback(this.JASON_INVENTORY_LEVELS_TABLES, params, this.getJasonAuthCandidates());
  }

  getJasonTableRows<T = any>(
    table: string,
    params: Record<string, string> = {}
  ): Observable<NowList<T>> {
    const authCandidates = this.getJasonAuthCandidates();
    return this.tryAuthCandidates(authCandidates, (auth) => this.getTableRows<T>(table, params, auth));
  }

  getClaireAuth(): SnApiAuth {
    return {
      user: this.DEMO_CLAIRE_USER,
      pass: this.DEMO_CLAIRE_PASS
    };
  }

  getJasonAuth(): SnApiAuth {
    return {
      user: this.DEMO_JASON_USER,
      pass: this.DEMO_JASON_PASS
    };
  }

  getJasonAuthCandidates(): SnApiAuth[] {
    const unique = new Map<string, SnApiAuth>();
    [this.getJasonAuth(), this.getIntegrationAuth(), this.getClaireAuth()].forEach((candidate) => {
      unique.set(candidate.user, candidate);
    });
    return Array.from(unique.values());
  }

  getIntegrationAuth(): SnApiAuth {
    return {
      user: this.integrationUser,
      pass: this.integrationPass
    };
  }

  getVendorOnboardingAuthCandidates(role?: string | null): SnApiAuth[] {
    const normalized = (role || '').trim().toLowerCase();
    const preferred = normalized === 'vendor manager' ? this.getClaireAuth() : this.getJasonAuth();
    const fallback = normalized === 'vendor manager' ? this.getJasonAuth() : this.getClaireAuth();
    const integration = this.getIntegrationAuth();
    const unique = new Map<string, SnApiAuth>();
    [preferred, fallback, integration].forEach((candidate) => {
      unique.set(candidate.user, candidate);
    });
    return Array.from(unique.values());
  }

  // Legacy direct browser endpoint retained for compatibility if still used.
  orderExtraction(variables: Record<string, any>, qty = 1): Observable<OrderNowResponse> {
    const url = `${this.localProxyBase}/api/sn_sc/servicecatalog/items/${this.CATALOG_ITEM_SYS_ID}/order_now`;
    const body: OrderNowRequest = { sysparm_quantity: String(qty), variables };
    return this.http.post<OrderNowResponse>(url, body);
  }

  private authToProfile(auth?: SnApiAuth): string {
    const user = String(auth?.user || '').trim().toLowerCase();
    if (user === 'demo.jason') return 'jason';
    if (user === 'demo.claire') return 'claire';
    if (user === 'nh-integrationuser') return 'integration';
    return '';
  }

  private withProfileParam(params: Record<string, string>, auth?: SnApiAuth): Record<string, string> {
    const next = { ...(params || {}) };
    const profile = this.authToProfile(auth);
    if (profile) next['profile'] = profile;
    return next;
  }

  private tryTables<T = any>(
    tables: string[],
    params: Record<string, string>,
    auth?: SnApiAuth
  ): Observable<NowList<T>> {
    if (!tables.length) {
      return throwError(() => new Error('No valid ServiceNow table found for Jason inventory levels.'));
    }
    const [head, ...rest] = tables;
    return this.getTableRows<T>(head, params, auth).pipe(
      catchError(() => this.tryTables(rest, params, auth))
    );
  }

  private tryTablesWithAuthFallback<T = any>(
    tables: string[],
    params: Record<string, string>,
    authCandidates: SnApiAuth[]
  ): Observable<NowList<T>> {
    if (!authCandidates.length) {
      return throwError(() => new Error('No ServiceNow auth candidates available for Jason inventory levels.'));
    }
    return this.tryAuthCandidates(authCandidates, (auth) => this.tryTables(tables, params, auth));
  }

  private tryAuthCandidates<T>(
    authCandidates: SnApiAuth[],
    requestFactory: (auth: SnApiAuth) => Observable<T>
  ): Observable<T> {
    if (!authCandidates.length) {
      return throwError(() => new Error('All ServiceNow auth candidates failed.'));
    }
    const [current, ...rest] = authCandidates;
    return requestFactory(current).pipe(
      catchError(() => this.tryAuthCandidates(rest, requestFactory))
    );
  }

  private withIdentityFromHeaders<T>(response: HttpResponse<NowSingle<T>>): NowSingle<T> {
    const body = response.body || ({ result: {} as T } as NowSingle<T>);
    const result = (body.result || {}) as Record<string, any>;

    const locationHeader = response.headers.get('location') || response.headers.get('Location') || '';
    const sysIdFromLocation = this.extractSysIdFromUrl(locationHeader);
    const sysIdFromLink = this.extractSysIdFromUrl(result?.['sys_id']?.link || result?.['link'] || '');

    const resolvedSysId = this.readIdentityValue(result?.['sys_id']) || sysIdFromLocation || sysIdFromLink;
    if (resolvedSysId && !this.readIdentityValue(result?.['sys_id'])) {
      result['sys_id'] = resolvedSysId;
      body.result = result as T;
    }

    return body;
  }

  private readIdentityValue(raw: any): string {
    if (raw === undefined || raw === null) return '';
    if (typeof raw === 'string' || typeof raw === 'number') return String(raw).trim();
    if (typeof raw === 'object') {
      const value = raw.value ?? raw.display_value ?? '';
      if (value !== undefined && value !== null && String(value).trim()) {
        return String(value).trim();
      }
      const fromLink = this.extractSysIdFromUrl(raw.link || raw.href || '');
      if (fromLink) return fromLink;
    }
    return '';
  }

  private extractSysIdFromUrl(url: string): string {
    const text = String(url || '').trim();
    if (!text) return '';
    const match = text.match(/\/([0-9a-f]{32})(?:[/?#]|$)/i);
    return match?.[1] || '';
  }
}
